/**
 * Customer 360 → Ministry CRM tab: the customer's linked Dynamics 365 contact
 * and their CRM activity (complaints, suggestions, appreciations, grievances,
 * cases), read live through the shared reader. Linking stores a local
 * gov_sync_mappings row only — nothing is ever written to Microsoft.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GlassButton, GlassCard, GlassInput, useI18n } from '@servicesai/ui';
import type { CrmCustomerPanel, CrmRecordDto, CrmStatus } from '@servicesai/api-client';
import { admin } from '../lib/api.js';

const FORMATTED_SUFFIX = '@OData.Community.Display.V1.FormattedValue';

const CASE_SET_LABELS: Record<string, { en: string; ar: string }> = {
  incidents: { en: 'Cases', ar: 'الحالات' },
  ldv_grievancerequests: { en: 'Grievances', ar: 'التظلمات' },
};

export function MinistryCrmTab({ customerId }: { customerId: string }) {
  const { l } = useI18n();
  const [status, setStatus] = useState<CrmStatus | null>(null);
  const [panel, setPanel] = useState<CrmCustomerPanel | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<CrmRecordDto[] | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [me, crmStatus] = await Promise.all([admin().me(), admin().crmStatus()]);
      setIsAdmin(me.roles.includes('admin'));
      setStatus(crmStatus);
      if (crmStatus.sso.connected && crmStatus.sharedReader.connected) {
        setPanel(await admin().crmCustomer(customerId));
      }
    } catch (loadError) {
      setError((loadError as Error).message);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const searchContacts = async () => {
    setBusy(true);
    try {
      const result = await admin().crmRecords('contacts', { search: contactSearch.trim() || undefined, top: 10 });
      setContactResults(result.records);
    } catch (searchError) {
      setError((searchError as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const linkContact = async (contactId: string) => {
    setBusy(true);
    try {
      await admin().crmLinkCustomer(customerId, contactId);
      setContactResults(null);
      setContactSearch('');
      await load();
    } catch (linkError) {
      setError((linkError as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    try {
      await admin().crmUnlinkCustomer(customerId);
      await load();
    } catch (unlinkError) {
      setError((unlinkError as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return <div className="h-48 animate-pulse rounded-xl border border-aeblack-100 bg-white" aria-busy="true" />;
  }

  if (!status.sso.connected) {
    return (
      <GlassCard className="p-6 text-center">
        <h3 className="text-h6 mb-2">{l({ en: 'Microsoft sign-in required', ar: 'مطلوب تسجيل الدخول عبر مايكروسوفت' })}</h3>
        <p className="mb-4 text-sm text-ink-soft">
          {l({
            en: 'The Ministry CRM tab shows this customer’s Dynamics 365 contact and activity. Sign in with Microsoft on the Ministry CRM page first.',
            ar: 'تعرض هذه اللوحة جهة اتصال المتعامل ونشاطه في دايناميكس 365. سجّل الدخول عبر مايكروسوفت من صفحة النظام الوزاري أولاً.',
          })}
        </p>
        <Link to="/crm" className="aegov-btn btn-primary btn-sm inline-flex min-h-10 items-center">
          {l({ en: 'Open Ministry CRM', ar: 'فتح النظام الوزاري' })}
        </Link>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="aegov-alert alert-error" role="alert">
          <div className="alert-content">{error}</div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="aegov-badge badge-success">{l({ en: 'Read-only', ar: 'قراءة فقط' })}</span>
        {status.mode === 'mock' && (
          <span className="aegov-badge badge-warning">{l({ en: 'Sandbox (mock)', ar: 'بيئة تجريبية' })}</span>
        )}
      </div>

      {!status.sharedReader.connected ? (
        <GlassCard className="p-5 text-sm text-ink-soft">
          {l({
            en: 'The shared CRM reader account is not connected — an administrator can connect it on the Ministry CRM page.',
            ar: 'حساب القارئ المشترك غير متصل — يمكن للمسؤول ربطه من صفحة النظام الوزاري.',
          })}
        </GlassCard>
      ) : !panel?.linked ? (
        <GlassCard className="p-5">
          <h3 className="text-h6 mb-1">{l({ en: 'No CRM contact linked', ar: 'لا توجد جهة اتصال مرتبطة' })}</h3>
          <p className="mb-4 text-sm text-ink-soft">
            {l({
              en: 'This customer is not linked to a Dynamics 365 contact yet.',
              ar: 'لم يُربط هذا المتعامل بعد بجهة اتصال في دايناميكس 365.',
            })}
          </p>
          {isAdmin ? (
            <div>
              <div className="mb-2 flex gap-2">
                <GlassInput
                  value={contactSearch}
                  onChange={(event) => setContactSearch(event.target.value)}
                  placeholder={l({ en: 'Search CRM contacts by name…', ar: 'ابحث في جهات الاتصال بالاسم…' })}
                  className="w-72"
                />
                <GlassButton size="sm" disabled={busy} onClick={() => void searchContacts()}>
                  {l({ en: 'Search', ar: 'بحث' })}
                </GlassButton>
              </div>
              {contactResults && (
                <ul className="space-y-1.5">
                  {contactResults.map((contact) => (
                    <li key={contact.id} className="flex items-center justify-between gap-2 rounded-lg border border-aeblack-100 bg-white px-3 py-1.5">
                      <div className="min-w-0">
                        <div className="truncate text-xs font-bold">{contact.title ?? contact.id}</div>
                        <div className="font-mono text-[10px] text-ink-faint">{contact.id}</div>
                      </div>
                      <GlassButton size="sm" disabled={busy} onClick={() => void linkContact(contact.id)}>
                        {l({ en: 'Link', ar: 'ربط' })}
                      </GlassButton>
                    </li>
                  ))}
                  {contactResults.length === 0 && (
                    <li className="text-xs text-ink-faint">{l({ en: 'No contacts found.', ar: 'لم يُعثر على جهات اتصال.' })}</li>
                  )}
                </ul>
              )}
            </div>
          ) : (
            <p className="text-xs font-semibold text-ink-faint">
              {l({ en: 'An administrator can link the CRM contact.', ar: 'يمكن للمسؤول ربط جهة الاتصال.' })}
            </p>
          )}
        </GlassCard>
      ) : (
        <>
          <GlassCard className="p-5">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-h6">{l({ en: 'Linked Dynamics 365 contact', ar: 'جهة الاتصال المرتبطة في دايناميكس 365' })}</h3>
                <div className="font-mono text-[10px] text-ink-faint" dir="ltr">{panel.contactId}</div>
              </div>
              {isAdmin && (
                <GlassButton size="sm" variant="ghost" disabled={busy} onClick={() => void unlink()}>
                  {l({ en: 'Unlink', ar: 'إلغاء الربط' })}
                </GlassButton>
              )}
            </div>
            {panel.contactError ? (
              <div className="aegov-alert alert-warning alert-sm" role="alert">
                <div className="alert-content">{panel.contactError}</div>
              </div>
            ) : panel.contact ? (
              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                <ContactField label={l({ en: 'Full name', ar: 'الاسم الكامل' })} value={panel.contact.fullname} />
                <ContactField label={l({ en: 'Email', ar: 'البريد الإلكتروني' })} value={panel.contact.emailaddress1} dir="ltr" />
                <ContactField label={l({ en: 'Mobile', ar: 'الهاتف المتحرك' })} value={panel.contact.mobilephone} dir="ltr" />
                <ContactField
                  label={l({ en: 'Created', ar: 'تاريخ الإنشاء' })}
                  value={
                    typeof panel.contact.createdon === 'string'
                      ? new Date(panel.contact.createdon).toLocaleDateString()
                      : null
                  }
                />
              </dl>
            ) : null}
          </GlassCard>

          {panel.cases.length === 0 ? (
            <GlassCard className="p-5 text-sm text-ink-soft">
              {l({ en: 'No CRM activity found for this contact.', ar: 'لا يوجد نشاط لهذه الجهة في النظام.' })}
            </GlassCard>
          ) : (
            panel.cases.map((group) => (
              <GlassCard key={group.set} className="p-5">
                <h4 className="text-h6 mb-2">
                  {l(CASE_SET_LABELS[group.set] ?? { en: group.set, ar: group.set })}
                  <span className="ms-2 text-xs font-semibold text-ink-faint">{group.records.length}</span>
                </h4>
                <ul className="space-y-1.5">
                  {group.records.map((record) => {
                    const statusLabel = record.raw[`statuscode${FORMATTED_SUFFIX}`];
                    const typeLabel = record.raw[`casetypecode${FORMATTED_SUFFIX}`];
                    return (
                      <li key={record.id} className="flex items-center justify-between gap-3 rounded-lg border border-aeblack-100 bg-white px-3 py-2">
                        <div className="min-w-0">
                          <div className="truncate text-xs font-bold">{record.title ?? record.id}</div>
                          <div className="font-mono text-[10px] text-ink-faint" dir="ltr">
                            {record.createdon ? new Date(record.createdon).toLocaleDateString() : '—'}
                          </div>
                        </div>
                        <span className="flex shrink-0 items-center gap-1.5">
                          {typeof typeLabel === 'string' && typeLabel.trim() && (
                            <span className="aegov-badge badge-secondary">{typeLabel.trim()}</span>
                          )}
                          {typeof statusLabel === 'string' && statusLabel && (
                            <span className="aegov-badge badge-info">{statusLabel}</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </GlassCard>
            ))
          )}
        </>
      )}
    </div>
  );
}

function ContactField({ label, value, dir }: { label: string; value: unknown; dir?: 'ltr' | 'rtl' }) {
  return (
    <div>
      <dt className="text-[10px] font-bold tracking-wide text-ink-faint uppercase">{label}</dt>
      <dd className="truncate font-semibold" dir={dir}>
        {typeof value === 'string' && value ? value : '—'}
      </dd>
    </div>
  );
}
